import React, { useState } from "react";
import PageHero from "../components/PageHero";
import { EditableText, EditableImage } from "../components/live/Editable";
import { MapPin, Phone, Loader2, CheckCircle2, Building, Bus, TrainFront, Plane, Car, TramFront } from "lucide-react";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";

const MAP_LINK =
  "https://maps.google.com/maps?ll=28.360862%2C76.916469&z=10&t=m&hl=en-US&gl=US&mapclient=embed&cid=17613659113676479500";

const ROUTES = [
  { icon: Car, label: "National Highway", text: "NH8, take exit for IMT Manesar. If traveling from Delhi, the exit is 5 kms after the Manesar Toll." },
  { icon: Bus, label: "Bus", text: "Haryana Roadways Bus connects IMT Manesar to Gurgaon Main City and the Metro Stations in Gurgaon." },
  { icon: TramFront, label: "Metro Station", text: "IFFCO Chowk, Gurgaon or Huda City Centre, Gurgaon — located about 20 kms (30 mins) away." },
  { icon: TrainFront, label: "Railway Station", text: "Local: Gahri Harsu. Gurgaon: about 10 kms (30 mins) away. National: New Delhi Railway Station, about 45 kms (2 hours) away." },
  { icon: Plane, label: "Airport", text: "New Delhi T3 or New Delhi T1 — located about 30 kms (45 mins) away." },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { toast } = useToast();

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast({ title: "Message sent!", description: "We'll get back to you soon." });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast({ title: "Failed", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero
        path="contact.hero"
        kicker="Contact Us"
        title="Contact Us"
        subtitle="Questions about courses, syllabus, placements or financial aid? We'd love to hear from you."
      />

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-8">
          {/* Info cards */}
          <div className="space-y-5">
            <InfoCard
              icon={MapPin}
              title="Address"
              lines={["Kiki Centre for Technology", "Institute: 1/47, Sector 75A,", "Gurugram - 122012"]}
              link={MAP_LINK}
              testid="contact-address-card"
              path="contact.address"
            />
            <InfoCard
              icon={Phone}
              title="Contact Numbers"
              lines={["Admission Office: +91-8800288994", "Institute, Administrative Office:", "0124-4060694/5"]}
              link="tel:+918800288994"
              testid="contact-phone-card"
              path="contact.phones"
            />
            <InfoCard
              icon={Building}
              title="Registered Office"
              lines={["Anjali Foundation", "159, Rabindra Sarani", "3rd Floor, Room No 3C", "Kolkata – 700007, India"]}
              testid="contact-registered-card"
              path="contact.registered"
            />
            <a
              href={MAP_LINK}
              target="_blank"
              rel="noreferrer"
              className="block text-center px-5 py-3 rounded-xl bg-[#0B2A6B] text-white font-semibold hover:bg-[#08205a] transition"
              data-testid="contact-map-link"
            >
              Find us on Google Maps
            </a>
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            {done ? (
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-2xl p-10 text-center">
                <CheckCircle2 className="h-14 w-14 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-[#0B2A6B] mb-2">Message Sent!</h3>
                <p className="text-slate-600 mb-6">We'll get back to you within 24 hours.</p>
                <Button onClick={() => setDone(false)} className="bg-[#0B2A6B] text-white">Send Another</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-7 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Name *"><Input required value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your name" data-testid="contact-name-input" /></Field>
                  <Field label="Email *"><Input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" data-testid="contact-email-input" /></Field>
                  <Field label="Phone"><Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91..." data-testid="contact-phone-input" /></Field>
                  <Field label="Subject"><Input value={form.subject} onChange={(e) => update("subject", e.target.value)} placeholder="How can we help?" data-testid="contact-subject-input" /></Field>
                </div>
                <Field label="Message *"><Textarea required rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us more..." data-testid="contact-message-input" /></Field>
                <Button type="submit" disabled={loading} className="w-full bg-yellow-400 hover:bg-yellow-500 text-[#0B2A6B] font-bold h-12 text-base" data-testid="contact-submit-btn">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Sending...</> : "Send Message"}
                </Button>
              </form>
            )}
            <EditableImage
              path="contact.main.image"
              src="/assets/kiki/contact-main.jpg"
              alt="Kiki Centre for Technology building"
              className="rounded-2xl shadow-lg overflow-hidden mt-8"
              imgClassName="w-full h-full object-cover"
              data-testid="contact-main-image"
            />
          </div>
        </div>
      </section>

      {/* How to reach */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <p className="kicker mb-3">How to reach us</p>
            <EditableText path="contact.reach.title" as="h2" className="text-3xl font-bold text-[#0B2A6B] mb-4 block">Conveniently located at IMT Manesar</EditableText>
            <EditableText path="contact.reach.text" as="p" multiline className="text-slate-600 leading-relaxed block" data-testid="contact-location-text">
              Kiki Centre for Technology is conveniently situated at the very well regarded Industrial Model Township of Manesar (IMT Manesar), Gurgaon, in the Delhi NCR. It is located 5 mins away from the Maruti Suzuki and Honda Motorcycle and Scooter factories in Manesar. It is well connected by the National Highway, Railway, Metro, Bus and Train.
            </EditableText>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ROUTES.map((r, i) => (
              <div
                key={r.label}
                className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm tilt-card animate-fade-up"
                style={{ animationDelay: `${i * 0.06}s` }}
                data-testid={`contact-route-${i}`}
              >
                <div className="h-12 w-12 rounded-xl bg-yellow-400 flex items-center justify-center mb-3">
                  <r.icon className="h-6 w-6 text-[#0B2A6B]" />
                </div>
                <EditableText path={`contact.route.${i}.label`} as="h3" className="font-bold text-[#0B2A6B] mb-1 block">{r.label}</EditableText>
                <EditableText path={`contact.route.${i}.text`} as="p" multiline className="text-sm text-slate-600 leading-relaxed block">{r.text}</EditableText>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function InfoCard({ icon: Icon, title, lines, link, testid, path }) {
  const content = (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-lg transition tilt-card" data-testid={testid}>
      <div className="h-12 w-12 rounded-xl bg-yellow-400 flex items-center justify-center mb-3">
        <Icon className="h-6 w-6 text-[#0B2A6B]" />
      </div>
      <EditableText path={`${path}.title`} as="h3" className="font-bold text-[#0B2A6B] mb-1 block">{title}</EditableText>
      {lines.map((l, i) => (
        <EditableText key={i} path={`${path}.line.${i}`} as="p" className="text-sm text-slate-600 block">{l}</EditableText>
      ))}
    </div>
  );
  return link ? <a href={link} target={link.startsWith("http") ? "_blank" : undefined} rel="noreferrer">{content}</a> : content;
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm font-semibold text-slate-700 mb-1.5 inline-block">{label}</Label>
      {children}
    </div>
  );
}
